import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/button/Button'
import { kDefaultComingSoonMessage } from './constants'

export type ComingSoonCardProps = {
  /** Large heading. Defaults to "Coming soon". */
  title?: string
  /** Supporting sentence under the heading. */
  message?: string
  /** Filled primary button label. Defaults to "Back to home". */
  actionLabel?: string
  /** Action handler. Defaults to navigating home (/dashboard). */
  onAction?: () => void
}

/**
 * Full-page "Coming soon" state, used when a feature exists but isn't available to the
 * current user yet (e.g. collaborators reaching an owner-only matter feature). Mirrors the
 * card shell used by ErrorCard, but with a single heading + message (no error code/title pair).
 */
export default function ComingSoonCard({
  title = 'Coming soon',
  message = kDefaultComingSoonMessage,
  actionLabel = 'Back to home',
  onAction,
}: ComingSoonCardProps) {
  const navigate = useNavigate()
  const handleAction = onAction ?? (() => navigate('/dashboard'))

  return (
    <div className="grid h-full w-full place-items-center bg-surface-content px-6 py-24">
      <div className="flex w-full max-w-[751px] flex-col">
        <div className="flex flex-col items-center rounded-lg border border-border-default bg-surface-emphasis px-12 py-14">
          <div className="flex w-full flex-col items-center gap-9 text-center">
            <div className="flex w-full flex-col gap-5">
              <p className="w-full font-rethink-sans text-5xl font-semibold leading-[normal] tracking-[-0.32px] text-text-primary">{title}</p>
              <p className="w-full text-xl font-normal leading-6 tracking-[-0.32px] text-text-secondary">{message}</p>
            </div>
            <Button type="button" variant="filled" color="primary" size="md" onClick={handleAction}>
              {actionLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
